import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFanAffinityLaws } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.fanAffinityLaws.v1';

type Law = '1' | '2' | '3';
type Fields = {
  law: Law;
  flow1: string;
  speed1: string;
  flow2: string;
  speed2: string;
  pressure1: string;
  speed1P: string;
  pressure2: string;
  speed2P: string;
  power1: string;
  speed1Pow: string;
  power2: string;
  speed2Pow: string;
};
const EMPTY: Fields = {
  law: '1',
  flow1: '', speed1: '', flow2: '', speed2: '',
  pressure1: '', speed1P: '', pressure2: '', speed2P: '',
  power1: '', speed1Pow: '', power2: '', speed2Pow: '',
};

/**
 * Fan Affinity Laws
 *   Law 1: Q2/Q1 = N2/N1
 *   Law 2: P2/P1 = (N2/N1)²
 *   Law 3: HP2/HP1 = (N2/N1)³
 */
const FanEquationsFanAffinityLawsScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculateLaw1 = useCallback(() => {
    const vals = [fields.flow1, fields.speed1, fields.flow2, fields.speed2];
    const filled = vals.filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of the 4 Law 1 fields.'); return; }
    if (filled === 4) { setError('All 4 Law 1 values are filled — clear one to solve for it.'); return; }
    const q1 = toImp(fields.flow1, 'flow');
    const n1 = parseFloat(fields.speed1);
    const q2 = toImp(fields.flow2, 'flow');
    const n2 = parseFloat(fields.speed2);

    if (fields.flow2 === '') {
      if (n1 === 0) { setError('Speed 1 cannot be zero.'); return; }
      setSolvedFor('flow2');
      set({ flow2: fromImp((q1 * n2) / n1, 'flow', 2) });
    } else if (fields.speed2 === '') {
      if (q1 === 0) { setError('Flow 1 cannot be zero.'); return; }
      setSolvedFor('speed2');
      set({ speed2: ((n1 * q2) / q1).toFixed(2) });
    } else if (fields.flow1 === '') {
      if (n2 === 0) { setError('Speed 2 cannot be zero.'); return; }
      setSolvedFor('flow1');
      set({ flow1: fromImp((q2 * n1) / n2, 'flow', 2) });
    } else if (fields.speed1 === '') {
      if (q2 === 0) { setError('Flow 2 cannot be zero.'); return; }
      setSolvedFor('speed1');
      set({ speed1: ((n2 * q1) / q2).toFixed(2) });
    }
  }, [fields, toImp, fromImp, set]);

  const calculateLaw2 = useCallback(() => {
    const vals = [fields.pressure1, fields.speed1P, fields.pressure2, fields.speed2P];
    const filled = vals.filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of the 4 Law 2 fields.'); return; }
    if (filled === 4) { setError('All 4 Law 2 values are filled — clear one to solve for it.'); return; }
    const p1 = toImp(fields.pressure1, 'pressure');
    const n1 = parseFloat(fields.speed1P);
    const p2 = toImp(fields.pressure2, 'pressure');
    const n2 = parseFloat(fields.speed2P);

    if (fields.pressure2 === '') {
      if (n1 === 0) { setError('Speed 1 cannot be zero.'); return; }
      setSolvedFor('pressure2');
      set({ pressure2: fromImp(p1 * Math.pow(n2 / n1, 2), 'pressure', 2) });
    } else if (fields.speed2P === '') {
      if (p1 === 0) { setError('Pressure 1 cannot be zero.'); return; }
      setSolvedFor('speed2P');
      set({ speed2P: (n1 * Math.sqrt(p2 / p1)).toFixed(2) });
    } else if (fields.pressure1 === '') {
      if (n2 === 0) { setError('Speed 2 cannot be zero.'); return; }
      setSolvedFor('pressure1');
      set({ pressure1: fromImp(p2 * Math.pow(n1 / n2, 2), 'pressure', 2) });
    } else if (fields.speed1P === '') {
      if (p2 === 0) { setError('Pressure 2 cannot be zero.'); return; }
      setSolvedFor('speed1P');
      set({ speed1P: (n2 * Math.sqrt(p1 / p2)).toFixed(2) });
    }
  }, [fields, toImp, fromImp, set]);

  const calculateLaw3 = useCallback(() => {
    const vals = [fields.power1, fields.speed1Pow, fields.power2, fields.speed2Pow];
    const filled = vals.filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of the 4 Law 3 fields.'); return; }
    if (filled === 4) { setError('All 4 Law 3 values are filled — clear one to solve for it.'); return; }
    const hp1 = toImp(fields.power1, 'power');
    const n1 = parseFloat(fields.speed1Pow);
    const hp2 = toImp(fields.power2, 'power');
    const n2 = parseFloat(fields.speed2Pow);

    if (fields.power2 === '') {
      if (n1 === 0) { setError('Speed 1 cannot be zero.'); return; }
      setSolvedFor('power2');
      set({ power2: fromImp(hp1 * Math.pow(n2 / n1, 3), 'power', 2) });
    } else if (fields.speed2Pow === '') {
      if (hp1 === 0) { setError('Power 1 cannot be zero.'); return; }
      setSolvedFor('speed2Pow');
      set({ speed2Pow: (n1 * Math.pow(hp2 / hp1, 1 / 3)).toFixed(2) });
    } else if (fields.power1 === '') {
      if (n2 === 0) { setError('Speed 2 cannot be zero.'); return; }
      setSolvedFor('power1');
      set({ power1: fromImp(hp2 * Math.pow(n1 / n2, 3), 'power', 2) });
    } else if (fields.speed1Pow === '') {
      if (hp2 === 0) { setError('Power 2 cannot be zero.'); return; }
      setSolvedFor('speed1Pow');
      set({ speed1Pow: (n2 * Math.pow(hp1 / hp2, 1 / 3)).toFixed(2) });
    }
  }, [fields, toImp, fromImp, set]);

  const calculate = useCallback(() => {
    setError(null);
    if (fields.law === '1') calculateLaw1();
    else if (fields.law === '2') calculateLaw2();
    else calculateLaw3();
  }, [fields.law, calculateLaw1, calculateLaw2, calculateLaw3]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() =>
    fields.law === '1'
      ? ([
          fields.flow1 && { label: 'Flow 1', value: fields.flow1, unit: unit('flow'), emphasis: solvedFor === 'flow1' },
          fields.speed1 && { label: 'Speed 1', value: fields.speed1, unit: 'RPM', emphasis: solvedFor === 'speed1' },
          fields.flow2 && { label: 'Flow 2', value: fields.flow2, unit: unit('flow'), emphasis: solvedFor === 'flow2' },
          fields.speed2 && { label: 'Speed 2', value: fields.speed2, unit: 'RPM', emphasis: solvedFor === 'speed2' },
        ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[])
      : fields.law === '2'
      ? ([
          fields.pressure1 && { label: 'Pressure 1', value: fields.pressure1, unit: unit('pressure'), emphasis: solvedFor === 'pressure1' },
          fields.speed1P && { label: 'Speed 1', value: fields.speed1P, unit: 'RPM', emphasis: solvedFor === 'speed1P' },
          fields.pressure2 && { label: 'Pressure 2', value: fields.pressure2, unit: unit('pressure'), emphasis: solvedFor === 'pressure2' },
          fields.speed2P && { label: 'Speed 2', value: fields.speed2P, unit: 'RPM', emphasis: solvedFor === 'speed2P' },
        ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[])
      : ([
          fields.power1 && { label: 'Power 1', value: fields.power1, unit: unit('power'), emphasis: solvedFor === 'power1' },
          fields.speed1Pow && { label: 'Speed 1', value: fields.speed1Pow, unit: 'RPM', emphasis: solvedFor === 'speed1Pow' },
          fields.power2 && { label: 'Power 2', value: fields.power2, unit: unit('power'), emphasis: solvedFor === 'power2' },
          fields.speed2Pow && { label: 'Speed 2', value: fields.speed2Pow, unit: 'RPM', emphasis: solvedFor === 'speed2Pow' },
        ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[]), [fields, unit, solvedFor]);

  return (
    <CalcShell
      title="Fan Affinity Laws"
      subtitle="Calculate fan performance changes with speed variations"
      formula={
        <>
          <FormulaLine>Law 1: Q₂/Q₁ = N₂/N₁</FormulaLine>
          <FormulaLine>Law 2: P₂/P₁ = (N₂/N₁)²</FormulaLine>
          <FormulaLine>{isImperial ? 'Law 3: HP₂/HP₁ = (N₂/N₁)³' : 'Law 3: kW₂/kW₁ = (N₂/N₁)³'}</FormulaLine>
          <FormulaNote>Flow varies with speed; pressure with speed²; power with speed³.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter={isImperial ? 'Formulas: Q₂/Q₁ = N₂/N₁ | P₂/P₁ = (N₂/N₁)² | HP₂/HP₁ = (N₂/N₁)³' : 'Formulas: Q₂/Q₁ = N₂/N₁ | P₂/P₁ = (N₂/N₁)² | kW₂/kW₁ = (N₂/N₁)³'}
      diagram={<DiagramFanAffinityLaws />}
      outputSlots={{
        cfm:
          fields.law === '1' && fields.flow2
            ? toImp(fields.flow2, 'flow')
            : fields.law === '1' && fields.flow1
            ? toImp(fields.flow1, 'flow')
            : undefined,
        static_pressure_iwg:
          fields.law === '2' && fields.pressure2
            ? toImp(fields.pressure2, 'pressure')
            : fields.law === '2' && fields.pressure1
            ? toImp(fields.pressure1, 'pressure')
            : undefined,
        bhp:
          fields.law === '3' && fields.power2
            ? toImp(fields.power2, 'power')
            : fields.law === '3' && fields.power1
            ? toImp(fields.power1, 'power')
            : undefined,
        fan_rpm:
          fields.law === '1' && fields.speed2
            ? parseFloat(fields.speed2)
            : fields.law === '2' && fields.speed2P
            ? parseFloat(fields.speed2P)
            : fields.law === '3' && fields.speed2Pow
            ? parseFloat(fields.speed2Pow)
            : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.flow1) {
          patch.flow1 = fromImp(values.cfm, 'flow', 2);
          patch.law = '1';
        }
        if (values.static_pressure_iwg != null && !fields.pressure1) {
          patch.pressure1 = fromImp(values.static_pressure_iwg, 'pressure', 2);
          patch.law = '2';
        }
        if (values.bhp != null && !fields.power1) {
          patch.power1 = fromImp(values.bhp, 'power', 2);
          patch.law = '3';
        }
        if (values.fan_rpm != null) {
          const rpm = values.fan_rpm.toFixed(2);
          if (fields.law === '1' && !fields.speed1) patch.speed1 = rpm;
          else if (fields.law === '2' && !fields.speed1P) patch.speed1P = rpm;
          else if (fields.law === '3' && !fields.speed1Pow) patch.speed1Pow = rpm;
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <SegmentedButtons
        value={fields.law}
        onValueChange={(v) => set({ law: v as Law })}
        buttons={[
          { value: '1', label: 'Law 1 Q/N' },
          { value: '2', label: 'Law 2 P/N²' },
          { value: '3', label: `Law 3 ${unit('power')}/N³` },
        ]}
        style={{ marginBottom: 16 }}
      />

      {fields.law === '1' && (
        <>
          <View style={{ marginBottom: 12 }}>
            <NumericField label={`Flow 1 (${unit('flow')})`} value={fields.flow1} onChangeText={(v) => set({ flow1: v })} rightText={unit('flow')} onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label="Speed 1 (RPM)" value={fields.speed1} onChangeText={(v) => set({ speed1: v })} rightText="RPM" onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label={`Flow 2 (${unit('flow')})`} value={fields.flow2} onChangeText={(v) => set({ flow2: v })} rightText={unit('flow')} onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label="Speed 2 (RPM)" value={fields.speed2} onChangeText={(v) => set({ speed2: v })} rightText="RPM" onSubmitEditing={calculate} />
          </View>
        </>
      )}
      {fields.law === '2' && (
        <>
          <View style={{ marginBottom: 12 }}>
            <NumericField label={`Pressure 1 (${unit('pressure')})`} value={fields.pressure1} onChangeText={(v) => set({ pressure1: v })} rightText={unit('pressure')} onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label="Speed 1 (RPM)" value={fields.speed1P} onChangeText={(v) => set({ speed1P: v })} rightText="RPM" onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label={`Pressure 2 (${unit('pressure')})`} value={fields.pressure2} onChangeText={(v) => set({ pressure2: v })} rightText={unit('pressure')} onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label="Speed 2 (RPM)" value={fields.speed2P} onChangeText={(v) => set({ speed2P: v })} rightText="RPM" onSubmitEditing={calculate} />
          </View>
        </>
      )}
      {fields.law === '3' && (
        <>
          <View style={{ marginBottom: 12 }}>
            <NumericField label={`Power 1 (${unit('power')})`} value={fields.power1} onChangeText={(v) => set({ power1: v })} rightText={unit('power')} onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label="Speed 1 (RPM)" value={fields.speed1Pow} onChangeText={(v) => set({ speed1Pow: v })} rightText="RPM" onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label={`Power 2 (${unit('power')})`} value={fields.power2} onChangeText={(v) => set({ power2: v })} rightText={unit('power')} onSubmitEditing={calculate} />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField label="Speed 2 (RPM)" value={fields.speed2Pow} onChangeText={(v) => set({ speed2Pow: v })} rightText="RPM" onSubmitEditing={calculate} />
          </View>
        </>
      )}
    </CalcShell>
  );
};

export default FanEquationsFanAffinityLawsScreen;
